import React, { useContext, useEffect } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";

const PreviewBar = ({ width, height = 8, radius = 1 }) => (
  <div
    className="nav-layout-preview-bar"
    style={{
      width,
      minHeight: height,
      height,
      borderRadius: radius,
      flexShrink: 0,
    }}
  />
);

const Grow = () => <div style={{ flex: 1, minWidth: 4 }} />;
const Logo = () => <PreviewBar width={12} height={12} radius={2} />;
const Link = () => <PreviewBar width={8} height={5} />;
const Custom = () => <PreviewBar width={16} height={10} radius={3} />;

const Links = () => (
  <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
    <Link key="1" />
    <Link key="2" />
    <Link key="3" />
  </div>
);

const Row = ({ children }) => (
  <div
    className="row hard-center"
    style={{ display: "flex", alignItems: "center", gap: 3, height: 16 }}
  >
    {children}
  </div>
);

const NAV_LAYOUTS = [
  {
    value: "links-right",
    label: "Links right",
    title: "Logo left, links right",
    preview: (
      <Row>
        <Logo />
        <Grow />
        <Links />
      </Row>
    ),
  },
  {
    value: "links-center",
    label: "Links center",
    title: "Logo left, links center",
    preview: (
      <Row>
        <Logo />
        <Grow />
        <Links />
        <Grow />
      </Row>
    ),
  },
  {
    value: "links-left",
    label: "Links left",
    title: "Logo left, links left",
    preview: (
      <Row>
        <Logo />
        <Links />
        <Grow />
      </Row>
    ),
  },
  {
    value: "custom-right",
    label: "Links right, custom right",
    title: "Logo left, custom content right, links right",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Grow />
        <Links />
        <Custom />
      </Row>
    ),
  },
  {
    value: "custom-center",
    label: "Links right, custom center",
    title: "Logo left, custom content center, links right",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Grow />
        <Custom />
        <Grow />
        <Links />
      </Row>
    ),
  },
  {
    value: "custom-left",
    label: "Links right, custom left",
    title: "Logo left, custom content left, links right",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Custom />
        <Grow />
        <Links />
      </Row>
    ),
  },
  {
    value: "links-center-custom-right",
    label: "Links center, custom right",
    title: "Logo left, links center, custom content right",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Grow />
        <Links />
        <Grow />
        <Custom />
      </Row>
    ),
  },
  {
    value: "links-center-custom-left",
    label: "Links center, custom left",
    title: "Logo left, custom content left, links center",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Custom />
        <Grow />
        <Links />
        <Grow />
      </Row>
    ),
  },
  {
    value: "links-left-custom-right",
    label: "Links left, custom right",
    title: "Logo left, links left, custom content right",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Links />
        <Grow />
        <Custom />
      </Row>
    ),
  },
  {
    value: "links-left-custom-center",
    label: "Links left, custom center",
    title: "Logo left, links left, custom content center",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Links />
        <Grow />
        <Custom />
        <Grow />
      </Row>
    ),
  },
  {
    value: "links-left-custom-left",
    label: "Links left, custom left",
    title: "Logo left, custom content left, links left",
    requiresCustom: true,
    preview: (
      <Row>
        <Logo />
        <Custom />
        <Links />
        <Grow />
      </Row>
    ),
  },
  {
    value: "stacked",
    label: "Links center, stacked",
    title: "Stacked, centered",
    preview: (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Logo />
        <PreviewBar width={20} height={5} />
        <PreviewBar width={16} height={5} />
      </div>
    ),
  },
];

const CUSTOM_LAYOUT_VALUES = [
  "custom-left",
  "custom-center",
  "custom-right",
  "links-center-custom-right",
  "links-center-custom-left",
  "links-left-custom-right",
  "links-left-custom-center",
  "links-left-custom-left",
];

const layoutForCustomContent = (value) => {
  if (CUSTOM_LAYOUT_VALUES.includes(value) || value == "stacked") {
    return value;
  }
  if (value == "links-center") {
    return "links-center-custom-right";
  }
  if (value == "links-left") {
    return "links-left-custom-right";
  }
  return "custom-right";
};

const layoutWithoutCustomContent = (value) => {
  if (!CUSTOM_LAYOUT_VALUES.includes(value)) {
    return value;
  }
  if (value.indexOf("links-center") == 0) {
    return "links-center";
  }
  if (value.indexOf("links-left") == 0) {
    return "links-left";
  }
  return "links-right";
};

const VariantBuilderSettingNavigationLayout = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const currentValue = props.value || "links-right";
  const elementId = props.object && props.object.id;
  const hasCustomContent = Object.values(
    (contextData.objects &&
      contextData.objects.variant &&
      contextData.objects.variant.content_toggle_items) ||
      {}
  ).some((item) => item.element_id == elementId && item.toBeDeleted !== true);
  const visibleLayouts = hasCustomContent
    ? NAV_LAYOUTS
    : NAV_LAYOUTS.filter((layout) => !layout.requiresCustom);
  const activeValue = hasCustomContent
    ? layoutForCustomContent(currentValue)
    : layoutWithoutCustomContent(currentValue);

  const updateValue = (value) => {
    builder.update([
      {
        object_type: props.object_type,
        object_id: props.object_id,
        setting_name: props.setting_name,
        value: value,
        skip_history: props.skip_history,
      },
    ]);
  };

  useEffect(() => {
    if (activeValue == currentValue) {
      return;
    }

    builder.update([
      {
        object_type: props.object_type,
        object_id: props.object_id,
        setting_name: props.setting_name,
        value: activeValue,
        skip_history: true,
      },
    ]);
  }, [hasCustomContent, activeValue, currentValue]);

  return (
    <div className="column-select-outer" setting_name={props.setting_name}>
      <div className="row hard-center">
        {visibleLayouts.map((layout) => {
          const isActive = activeValue == layout.value;

          return (
            <div
              className="col-xs-6"
              key={layout.value}
              style={{ marginBottom: "8px", padding: "0px" }}
            >
              <button
                type="button"
                data-value={layout.value}
                title={layout.title}
                className={`btn btn-default column-select-button nav-layout-select-button${
                  isActive ? " column-select-button-active" : ""
                }`}
                onClick={() => updateValue(layout.value)}
              >
                <div
                  className="row hard-center column-select-option"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 8px",
                    margin: 0,
                  }}
                >
                  {layout.preview}
                </div>
                <div className="nav-layout-select-label">{layout.label}</div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VariantBuilderSettingNavigationLayout;
